import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { Tenant, TenantState } from "./tenant.types";
import { loadStoredSession } from "../auth/auth.storage";
import { createDemoTenant } from "../auth/auth.mapper";
import { useAuth } from "../auth/useAuth";
import {
  buildTenantHostname,
  resolveTenantSlugFromHostname,
} from "./platform-domain";

export interface TenantContextValue extends TenantState {
  setTenant: (tenant: Tenant | null) => void;
  resolveTenantFromSubdomain: (hostname: string) => Tenant;
}

export const TenantContext = createContext<TenantContextValue | null>(null);

const MULTI_TENANT_ENABLED =
  import.meta.env.VITE_ITAAS_MULTI_TENANT === "true";

const MOCK_TENANT: Tenant = createDemoTenant();

interface TenantProviderProps {
  children: React.ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const { tenant: authTenant, isAuthenticated } = useAuth();
  const [state, setState] = useState<TenantState>({
    tenant: null,
    isLoading: true,
    isMultiTenantEnabled: MULTI_TENANT_ENABLED,
  });

  const resolveTenantFromSubdomain = useCallback(
    (hostname: string): Tenant => {
      if (!MULTI_TENANT_ENABLED) return MOCK_TENANT;

      const slug = resolveTenantSlugFromHostname(hostname);
      if (!slug) return MOCK_TENANT;

      return createDemoTenant({
        id: `tenant-${slug}`,
        name: slug.charAt(0).toUpperCase() + slug.slice(1),
        slug,
        domain: buildTenantHostname(slug),
      });
    },
    []
  );

  const setTenant = useCallback((tenant: Tenant | null) => {
    setState((prev) => ({ ...prev, tenant }));
  }, []);

  useEffect(() => {
    const authSession = loadStoredSession();
    let tenant: Tenant | null = authSession?.tenant ?? null;

    if (!tenant && typeof window !== "undefined") {
      tenant = resolveTenantFromSubdomain(window.location.hostname);
    }

    setState({
      tenant,
      isLoading: false,
      isMultiTenantEnabled: MULTI_TENANT_ENABLED,
    });
  }, [resolveTenantFromSubdomain]);

  useEffect(() => {
    if (isAuthenticated && authTenant) {
      setState((prev) => ({ ...prev, tenant: authTenant }));
    }
  }, [isAuthenticated, authTenant]);

  const value = useMemo<TenantContextValue>(
    () => ({
      ...state,
      setTenant,
      resolveTenantFromSubdomain,
    }),
    [state, setTenant, resolveTenantFromSubdomain]
  );

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
};
